import SummarySection from "features/bots/BotForm/Summaries/SummarySection";
import React, { CSSProperties, useContext } from "react";
import { CoinGroupSignalsData } from "@shared/features/onboarding/types";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Text from "common/Typography/Text";
import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES } from "@shared/common/constants";
import styled, { useTheme } from "styled-components";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import useGroupMainCoinsSignalsCount from "@shared/features/bots/BotForm/useGroupMainCoinsSignalsCount.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";

interface SignalsSummaryProps {
  style?: CSSProperties;
  hideTitle?: boolean;
}

const SignalsSummary = ({ style, hideTitle }: SignalsSummaryProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const { signalsMainCoinsData } = useContext(BotFormContext)!;
  const {
    sortedMainCoinsData,
    showEmptyState,
    emptyStateText,
    title,
    helperData,
  } = signalsMainCoinsData || {};
  const { isFetching } = useGroupMainCoinsSignalsCount({});

  const spans = [7, 8, 9];

  const getCoinRow = (coinData: CoinGroupSignalsData) => {
    return (
      <Row
        gutter={[4, 4]}
        style={{ width: "100%", padding: "2px 10px" }}
        key={coinData.coin}
      >
        <Col
          span={spans[0]}
          style={{
            display: "flex",
            alignItems: "center",
            paddingRight: 8,
          }}
        >
          <TextWithIcon
            text={coinData.coin}
            coinOrExchangeName={coinData.coin}
            iconType={ICON_TYPES.COIN}
            textStyle={{ color: theme.colors.primaryText }}
          />
        </Col>
        <Col span={spans[1]} style={{ display: "flex", alignItems: "center" }}>
          <Text>{coinData.numOfSignals}</Text>
        </Col>
        <Col span={spans[2]}>
          <Text>{coinData.total} USD</Text>
          <Text>({coinData.available} USD)</Text>
        </Col>
      </Row>
    );
  };

  if (theme.isNewLook) {
    // One plain table: single-line headers on a hairline, numbers right
    // aligned, and a centred note (without the headers) when there is nothing
    // to list.
    return (
      <LoadingSpinnerOverlay
        isLoading={!!isFetching}
        backgroundColor={theme.colors.cardBgColor}
      >
        <div style={style}>
          {showEmptyState ? (
            <NewLookEmpty>{emptyStateText}</NewLookEmpty>
          ) : (
            <NewLookTable>
              <NewLookHead>
                <span>{t("most_traded_pair.columns.pair")}</span>
                <span>
                  {t("most_traded_pair.columns.signals")}
                  <HeadNote>{t("most_traded_pair.columns.30_days")}</HeadNote>
                </span>
                <span>
                  {t("most_traded_pair.columns.total")}
                  <HeadNote>{t("most_traded_pair.columns.available")}</HeadNote>
                </span>
              </NewLookHead>
              {sortedMainCoinsData?.map((coinData) => (
                <NewLookRow key={coinData.coin}>
                  <TextWithIcon
                    text={coinData.coin}
                    coinOrExchangeName={coinData.coin}
                    iconType={ICON_TYPES.COIN}
                    textStyle={{ color: theme.colors.titleColor }}
                  />
                  <NewLookValue>{coinData.numOfSignals}</NewLookValue>
                  <NewLookValue>
                    {coinData.total} USD
                    <NewLookSubValue>
                      ({coinData.available} USD)
                    </NewLookSubValue>
                  </NewLookValue>
                </NewLookRow>
              ))}
            </NewLookTable>
          )}
        </div>
      </LoadingSpinnerOverlay>
    );
  }

  return (
    <SummarySection
      isCard={false}
      columnNames={[
        t("most_traded_pair.columns.pair"),
        <div>
          <div>{t("most_traded_pair.columns.signals")}</div>
          <div>{t("most_traded_pair.columns.30_days")}</div>
        </div>,
        <div>
          <div>{t("most_traded_pair.columns.total")}</div>
          <div>{t("most_traded_pair.columns.available")}</div>
        </div>,
      ]}
      columnSpans={spans}
      title={!hideTitle && title}
      sorterStyle={{ fontSize: 12, color: theme.colors.primaryText }}
      containerStyle={{ padding: 0, ...style }}
      tooltipProps={helperData}
      isFetching={isFetching}
      loadingProps={{ backgroundColor: theme.colors.cardBgColor }}
    >
      {showEmptyState ? (
        <Text
          type="secondary"
          style={{ textAlign: "center", padding: "12px 4px" }}
        >
          {emptyStateText}
        </Text>
      ) : (
        sortedMainCoinsData?.map(getCoinRow)
      )}
    </SummarySection>
  );
};

export default SignalsSummary;

const newLookColumns = `
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.15fr);
  column-gap: 12px;
  align-items: center;
  & > :not(:first-child) {
    text-align: right;
  }
`;
const NewLookTable = styled.div`
  display: flex;
  flex-direction: column;
`;
const NewLookHead = styled.div`
  ${newLookColumns};
  padding: 0 0 10px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  align-items: end;
  > span {
    min-width: 0;
  }
`;
// The qualifier ("(30 days)", "(Available)") on its own quiet line, so the
// header never truncates in a narrow drawer.
const HeadNote = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
`;
const NewLookRow = styled.div`
  ${newLookColumns};
  min-height: 56px;
  padding: 8px 0;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const NewLookValue = styled.div`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const NewLookSubValue = styled.div`
  margin-top: 2px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
// Inside the panel: one quiet line, no icon.
const NewLookEmpty = styled.div`
  padding: 24px 12px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  text-align: center;
`;
